import {Controller} from "@hotwired/stimulus"
import flatpickr from "flatpickr";

// Connects to data-controller="date-range-filter"
export default class extends Controller {
  static targets = ["dateInput", "clearDateBtn"]

  static values = {
    startDate: String,
    endDate: String,
    minDate: String,
    maxDate: String,
    containerId: { type: String, default: "recurringDateContainer" }
  }

  // Properties to store date values in memory
  get startDate() {
    return this.startDateValue
  }

  set startDate(value) {
    this.startDateValue = value
  }

  get endDate() {
    return this.endDateValue
  }

  set endDate(value) {
    this.endDateValue = value
  }

  connect() {
    if (this.hasDateInputTarget) {
      try {
        const config = this.buildFlatpickrConfig();
        this.flatpickrInstance = flatpickr(this.dateInputTarget, config);
      } catch (error) {
        console.error('Failed to initialize flatpickr:', error);
      }
    }
  }

  disconnect() {
    if (this.flatpickrInstance) {
      this.flatpickrInstance.destroy();
      this.flatpickrInstance = null;
    }
  }

  initialStartEndDate() {
    const dateContainer = document.getElementById(this.containerIdValue)
    if (dateContainer) {
      // Initialize from dataset if values aren't already set
      if (!this.startDateValue && dateContainer.dataset.startdate) {
        this.startDateValue = dateContainer.dataset.startdate
      }
      if (!this.endDateValue && dateContainer.dataset.enddate) {
        this.endDateValue = dateContainer.dataset.enddate
      }
    }
  }

  buildFlatpickrConfig() {
    const config = {
      mode: "range",
      altInput: true,
      altFormat: "m-d-Y",
      dateFormat: "Y-m-d",
      defaultDate: [this.startDateValue, this.endDateValue].filter(Boolean),
      onChange: this.handleDateChange.bind(this),
    }

    if (this.minDateValue) {
      config.minDate = this.minDateValue;
    }

    if (this.maxDateValue) {
      config.maxDate = this.maxDateValue;
    }

    return config;
  }

  handleDateChange(selectedDates) {
    if (selectedDates.length > 0) {
      this.startDateValue = this.formatDate(selectedDates[0])
      this.endDateValue = selectedDates[1] ? this.formatDate(selectedDates[1]) : ''

      // Update dataset for backward compatibility
      const dateContainer = document.getElementById(this.containerIdValue)
      if (dateContainer) {
        dateContainer.dataset.startdate = this.startDateValue
        dateContainer.dataset.enddate = this.endDateValue
      }

      // Show clear button when dates are selected
      if (this.hasClearDateBtnTarget) {
        this.clearDateBtnTarget.classList.remove('d-none')
      }
    } else {
      // Clear values when no dates are selected
      this.startDateValue = ''
      this.endDateValue = ''
      
      // Hide clear button when no dates are selected
      if (this.hasClearDateBtnTarget) {
        this.clearDateBtnTarget.classList.add('d-none')
      }
    }

    // Trigger dataTable redraw if it exists
    if (this.dataTable) {
      this.dataTable.draw()
    }
  }

  formatDate(date) {
    if (!date) return ''
    const d = new Date(date)
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')  // Months are 0-based
    const day = String(d.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`  // Returns YYYY-MM-DD format
  }

  clearDateFilter() {
    try {
      // Clear the flatpickr instance
      if (this.flatpickrInstance) {
        this.flatpickrInstance.clear()
      }

      // Clear stored dates using Stimulus values
      this.startDateValue = ''
      this.endDateValue = ''

      // Clear dataset values for backward compatibility
      const dateContainer = document.getElementById(this.containerIdValue)
      if (dateContainer) {
        dateContainer.dataset.startdate = ''
        dateContainer.dataset.enddate = ''
      }

      // Hide clear button
      if (this.hasClearDateBtnTarget) {
        this.clearDateBtnTarget.classList.add('d-none')
      }

      // Redraw the datatable if it exists
      if (this.dataTable) {
        this.dataTable.draw()
      }
    } catch (error) {
      console.error('Failed to clear date filter:', error);
      // Attempt basic cleanup even if there's an error
      this.startDateValue = ''
      this.endDateValue = ''
    }
  }

  // Value changed callbacks for reactive updates
  startDateValueChanged() {
    this.updateDateContainer()
  }

  endDateValueChanged() {
    this.updateDateContainer()
  }

  updateDateContainer() {
    const dateContainer = document.getElementById(this.containerIdValue)
    if (dateContainer) {
      dateContainer.dataset.startdate = this.startDateValue || ''
      dateContainer.dataset.enddate = this.endDateValue || ''
    }
  }
}